import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff, ExternalLink, Lock, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import { useMeta } from "@/contexts/MetaContext";
import { playerLink } from "@/components/PlayerCard";
import Sparkline, { DeltaChip } from "@/components/Sparkline";
import { milestoneLabel } from "@/milestones";

const mono = "font-mono2 tracking-[.04em]";
const btn = `${mono} border border-[var(--d-line)] px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--d-text)] transition-colors hover:border-[var(--d-accent)] hover:text-[var(--d-accent)] disabled:opacity-40`;
const btnAccent = `${mono} border border-[var(--d-accent)] bg-[var(--d-accent)] px-3 py-1.5 text-[10px] font-bold uppercase text-[#0B0E14] transition-colors hover:bg-transparent hover:text-[var(--d-accent)] disabled:opacity-40`;
const btnDanger = `${mono} border border-red-500/60 px-3 py-1.5 text-[10px] font-bold uppercase text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-40`;

const fmtDate = (v, lang) => (v ? new Date(v).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");

function Block({ title, testid, meta, children }) {
  return (
    <section data-testid={testid} className="border border-[var(--d-line)] bg-[var(--d-panel)]">
      <header className="flex items-center justify-between gap-3 border-b border-[var(--d-line)] px-4 py-2.5">
        <h2 className="font-sg text-sm font-bold uppercase tracking-[-0.02em] text-[var(--d-text)]">{title}</h2>
        {meta && <span className={`${mono} text-[9px] uppercase text-[var(--d-muted)]`}>{meta}</span>}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function Row({ label, value, testid, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--d-line)] py-2 last:border-b-0">
      <span className={`${mono} text-[10px] uppercase text-[var(--d-muted)]`}>{label}</span>
      <span data-testid={testid} className={`${mono} text-right text-xs ${accent ? "font-bold text-[var(--d-accent)]" : "text-[var(--d-text)]"}`}>{value}</span>
    </div>
  );
}

const Empty = ({ children, testid }) => <p data-testid={testid} className={`${mono} py-1 text-[11px] text-[var(--d-muted)]`}>{children}</p>;

export default function DashboardPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const meta = useMeta();
  const freeLimit = meta?.free_list_limit ?? 1;
  const [preview, setPreview] = useState(null);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dashboard");
      setD(r.data);
    } catch {
      navigate("/auth");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleNotify = async (w) => {
    try {
      await api.put("/lists/notify", { player_id: w.player_id, provider: w.provider, notify: !w.notify });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeFollow = async (w) => {
    try {
      await api.post("/lists/toggle", { player_id: w.player_id, provider: w.provider });
      toast.success(t("dash.removed_ok", { name: w.name || w.player?.name || `#${w.player_id}` }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/reactivate");
      toast.success(t("dash.reactivated_ok", { date: fmtDate(r.data.renews_at, lang) }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      toast.success(t("dash.cancelled_ok", { date: fmtDate(r.data.access_until, lang) }));
      setCancelAsk(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const askSwitch = async () => {
    const target = d.billing.subscription.interval === "month" ? "annual" : "monthly";
    setBusy(true);
    try {
      const r = await api.get(`/billing/switch-preview?plan=${target}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmSwitch = async () => {
    setBusy(true);
    try {
      await api.post("/billing/switch-plan", { plan: preview.plan });
      toast.success(t("dash.switched_ok"));
      setPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!d) return <p className={`pt-16 text-center ${mono} text-sm text-slate-500`}>{t("common.loading")}</p>;

  const premium = d.premium;
  const sub = d.billing.subscription;
  const state = d.billing.stripe_state || "none";
  const period = sub?.interval === "year" ? t("dash.period_annual") : t("dash.period_monthly");

  return (
    <div className="dash-scope mx-auto max-w-[1200px] pt-8" data-testid="dashboard-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-[var(--d-line)] pb-4">
        <div>
          <h1 className="font-sg text-3xl font-bold uppercase tracking-[-0.035em] text-[var(--d-text)] sm:text-4xl">{t("dash.title")}</h1>
          <p className={`${mono} mt-1 text-[10px] uppercase text-[var(--d-muted)]`} data-testid="dash-email">{d.email}</p>
        </div>
        <span data-testid="dash-plan-value" className={`${mono} border px-2 py-0.5 text-[10px] font-bold uppercase ${premium ? "border-[var(--d-accent)] text-[var(--d-accent)]" : "border-[var(--d-line)] text-[var(--d-muted)]"}`}>
          {premium ? "Premium" : t("dash.plan_free")}
        </span>
      </div>

      {d.profile_complete === false && (
        <Link to="/account" data-testid="profile-incomplete-banner" className={`${mono} mb-6 block border border-[var(--d-line)] border-l-2 border-l-[var(--d-accent)] bg-[var(--d-panel)] px-3 py-2 text-[10px] uppercase text-[var(--d-text)] hover:text-[var(--d-accent)]`}>
          {t("dash.profile_banner")} →
        </Link>
      )}

      <div className="grid grid-cols-1 items-start gap-6 min-[900px]:grid-cols-2">
        <Block title={t("dash.subscription")} testid="dash-subscription">
          {!sub ? (
            <div data-testid="dash-free-plan-block">
              <Row label={t("dash.status")} value={premium ? t("premium.access_active") : t("dash.plan_free")} />
              {d.access_until && <Row label={t("dash.access_until")} value={fmtDate(d.access_until, lang)} testid="dash-access-until" accent />}
              {!premium && (
                <>
                  <p className={`${mono} mt-3 text-[11px] text-[var(--d-muted)]`}>{t("dash.free_features", { n: freeLimit })}</p>
                  <Link to="/pricing" data-testid="dash-upgrade-cta" className={`${btnAccent} mt-3 inline-block`}>{t("dash.upgrade_cta")}</Link>
                  <p className={`${mono} mt-2 text-[10px] text-[var(--d-muted)]`}>{t("dash.premium_adds")}</p>
                </>
              )}
            </div>
          ) : (
            <>
              <Row label={t("dash.plan_label")} value={`Premium · ${period}`} accent />
              <Row label={t("dash.stripe_state")} testid="dash-stripe-state" value={
                <span className={state === "past_due" ? "text-[#FFD600]" : state === "canceled" ? "text-red-400" : ""}>{t(`dash.state_${state}`)}</span>
              } />
              <Row label={t("dash.amount")} value={`${sub.amount} ${sub.currency} / ${period.toLowerCase()}`} />
              {state === "canceled" ? (
                <Row label={t("dash.ended_at")} value={fmtDate(sub.ended_at, lang)} testid="dash-renewal-date" />
              ) : (
                <Row label={state === "cancelling" ? t("dash.access_until") : t("dash.next_renewal")} value={fmtDate(sub.renews_at, lang)} testid="dash-renewal-date" />
              )}
              <Row label={t("dash.pm")} value={d.billing.payment_method ? `${d.billing.payment_method.brand} •••• ${d.billing.payment_method.last4}` : "—"} testid="dash-payment-method" />
              {state === "past_due" && <p className={`${mono} mt-2 border-l-2 border-[#FFD600] pl-2 text-[10px] text-[#FFD600]`} data-testid="past-due-note">{t("dash.past_due_note")}</p>}
              {sub?.grandfathered && <p className={`${mono} mt-2 text-[9px] uppercase text-[var(--d-muted)]`} data-testid="grandfathered-note">{t("dash.grandfathered_note")}</p>}
              {d.billing.scheduled_plan && (
                <p className={`${mono} mt-2 text-[10px] text-[var(--d-accent)]`} data-testid="scheduled-plan-note">
                  {t("dash.scheduled_plan_note", { plan: d.billing.scheduled_plan, date: fmtDate(d.billing.scheduled_plan_at, lang) })}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {/* A cancelled subscription has nothing to manage: offer the action that matches its state. */}
                {state === "cancelling" ? (
                  <button onClick={reactivate} disabled={busy} data-testid="reactivate-sub-button" className={btnAccent}>
                    {t("dash.reactivate_sub")}
                  </button>
                ) : state !== "canceled" ? (
                  <>
                    <button onClick={openPortal} data-testid="portal-button" className={btn}>{t("dash.manage_pm")}</button>
                    <button onClick={askSwitch} disabled={busy} data-testid="switch-plan-button" className={btn}>
                      {sub.interval === "month" ? t("dash.switch_to_annual") : t("dash.switch_to_monthly")}
                    </button>
                    <button onClick={() => setCancelAsk(true)} data-testid="cancel-sub-button" className={btnDanger}>{t("dash.cancel_sub")}</button>
                  </>
                ) : null}
                {state === "canceled" && <Link to="/pricing" data-testid="dash-resubscribe-cta" className={btnAccent}>{t("dash.resubscribe")}</Link>}
              </div>

              {preview && (
                <div className="mt-4 border border-[var(--d-line)] border-l-2 border-l-[var(--d-accent)] p-3" data-testid="switch-preview-panel">
                  <p className={`${mono} mb-2 text-[10px] font-bold uppercase text-[var(--d-accent)]`}>{t("dash.switch_preview_title")}</p>
                  <Row label={t("dash.switch_price")} value={`${preview.price_amount} ${preview.currency} / ${preview.interval === "year" ? t("dash.period_annual").toLowerCase() : t("dash.period_monthly").toLowerCase()}`} />
                  <Row label={t("dash.switch_due_now")} value={`${preview.due_now} ${preview.preview_currency || preview.currency}`} testid="switch-due-now" accent />
                  <p className={`${mono} mt-2 text-[10px] text-[var(--d-muted)]`}>
                    {preview.mode === "immediate" ? t("dash.switch_immediate") : t("dash.switch_scheduled", { date: fmtDate(preview.effective_at, lang) })}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={confirmSwitch} disabled={busy} data-testid="switch-confirm-button" className={btnAccent}>{t("dash.confirm")}</button>
                    <button onClick={() => setPreview(null)} data-testid="switch-abort-button" className={btn}>{t("dash.cancel")}</button>
                  </div>
                </div>
              )}

              {cancelAsk && sub && (
                <div className="mt-4 border border-red-500/40 border-l-2 border-l-red-500 p-3" data-testid="cancel-confirm-panel">
                  <p className={`${mono} text-[11px] text-[var(--d-text)]`}>{t("dash.cancel_confirm", { date: fmtDate(sub.renews_at, lang) })}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={confirmCancel} disabled={busy} data-testid="cancel-confirm-button" className={btnDanger}>{t("dash.cancel_sub")}</button>
                    <button onClick={() => setCancelAsk(false)} data-testid="cancel-abort-button" className={btn}>{t("dash.cancel")}</button>
                  </div>
                </div>
              )}

              <div className="mt-4 border-t border-[var(--d-line)] pt-3" data-testid="dash-invoices">
                <p className={`${mono} mb-1 text-[9px] uppercase text-[var(--d-muted)]`}>{t("dash.invoices")}</p>
                {d.billing.invoices.length === 0 && <Empty testid="dash-invoices-empty">{t("dash.invoices_none")}</Empty>}
                {d.billing.invoices.map((inv, i) => (
                  <div key={i} className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-b border-[var(--d-line)] py-1.5 ${mono} text-[10px] text-[var(--d-muted)] last:border-b-0`} data-testid="dash-invoice-row">
                    <span className="truncate text-[var(--d-text)]">{inv.created ? inv.created.slice(0, 10) : "—"} · {inv.number || "—"}</span>
                    <span className="text-[var(--d-text)]">{inv.amount} {inv.currency}</span>
                    <span className="uppercase">{inv.status}</span>
                    {inv.pdf ? <a href={inv.pdf} target="_blank" rel="noreferrer" data-testid={`invoice-pdf-${i}`} className="flex items-center gap-1 text-[var(--d-accent)] hover:underline">PDF <ExternalLink className="h-2.5 w-2.5" /></a> : <span>—</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </Block>

        <Block title={t("dash.watchlist_block")} testid="dash-watchlist" meta={`${d.watchlist.length} ${t("dash.used")}`}>
          {d.watchlist.length === 0 && (
            <div data-testid="dash-watchlist-empty">
              <Empty>{t("dash.watchlist_empty")}</Empty>
              <Link to="/watchlist" className={`${mono} text-[10px] font-bold uppercase text-[var(--d-accent)] hover:underline`} data-testid="dash-watchlist-empty-cta">{t("dash.watchlist_empty_cta")}</Link>
            </div>
          )}
          {d.watchlist.map((w) => (
            <div key={`${w.provider}:${w.player_id}`} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-b border-[var(--d-line)] py-2 last:border-b-0" data-testid="dash-watch-row">
              <div className="min-w-0">
                {w.player ? (
                  <Link to={playerLink(w.player)} data-testid={`dash-watch-link-${w.player_id}`} className="block truncate font-sg text-[13px] font-bold uppercase tracking-[-0.01em] text-[var(--d-text)] hover:text-[var(--d-accent)]">{w.player.name}</Link>
                ) : (
                  <span data-testid={`dash-watch-name-${w.player_id}`} className="block truncate font-sg text-[13px] font-bold uppercase tracking-[-0.01em] text-[var(--d-muted)]">{w.name || t("dash.player_gone")}</span>
                )}
                <p className={`${mono} truncate text-[9px] uppercase text-[var(--d-muted)]`}>
                  {w.stale ? t("dash.player_gone_hint") : (
                    <>
                      {w.player?.club_name || "—"}
                      {" · "}
                      {w.latest_milestone ? `${milestoneLabel(w.latest_milestone, t)}${w.latest_milestone.date ? ` · ${w.latest_milestone.date}` : ""}` : t("dash.no_milestone")}
                    </>
                  )}
                </p>
              </div>
              <span className={`${mono} text-right text-xs font-bold text-[var(--d-accent)]`} title={t("dash.hype_label")} data-testid={`dash-watch-hype-${w.player_id}`}>
                <span className="mr-1 text-[8px] font-normal uppercase text-[var(--d-muted)]">{t("dash.hype_label")}</span>{w.player?.signal_score ?? "—"}
              </span>
              {premium ? (
                <button onClick={() => toggleNotify(w)} data-testid={`notify-toggle-${w.provider}-${w.player_id}`}
                  className={`${mono} flex items-center gap-1 border px-2 py-1 text-[9px] font-bold uppercase transition-colors ${w.notify ? "border-[var(--d-accent)] text-[var(--d-accent)]" : "border-[var(--d-line)] text-[var(--d-muted)] hover:text-[var(--d-text)]"}`}>
                  {w.notify ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}{t("dash.notify")}
                </button>
              ) : (
                <span data-testid={`notify-locked-${w.provider}-${w.player_id}`} className={`${mono} flex items-center gap-1 border border-dashed border-[var(--d-line)] px-2 py-1 text-[9px] uppercase text-[var(--d-muted)]`}>
                  <Lock className="h-3 w-3" />{t("dash.notify")}
                </span>
              )}
              <button
                onClick={() => removeFollow(w)}
                data-testid={`dash-watch-remove-${w.provider}-${w.player_id}`}
                title={t("dash.remove")}
                aria-label={t("dash.remove")}
                className={`${mono} flex items-center gap-1 border border-[var(--d-line)] px-2 py-1 text-[9px] font-bold uppercase text-[var(--d-muted)] transition-colors hover:border-red-500/60 hover:text-red-400`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </Block>

        <Block title={t("dash.settings_block")} testid="dash-settings">
          <Link to="/account" data-testid="dash-profile-link" className={`${mono} flex items-center justify-between text-[11px] uppercase text-[var(--d-text)] hover:text-[var(--d-accent)]`}>
            <span>{t("dash.settings_link")}</span><span>→</span>
          </Link>
        </Block>

        {premium && (
          <>
            <Block title={t("dash.alerts_block")} testid="dash-alerts">
              {d.alerts.length === 0 && <Empty testid="dash-alerts-empty">{t("dash.alert_none")}</Empty>}
              {d.alerts.map((a, i) => (
                <div key={i} className={`grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-b border-[var(--d-line)] py-1.5 ${mono} text-[10px] last:border-b-0`} data-testid="dash-alert-row">
                  <span className="text-[var(--d-muted)]">{a.created_at ? a.created_at.slice(0, 10) : "—"}</span>
                  <span className="truncate text-[var(--d-text)]">{(a.params || {}).player || "—"}</span>
                  <span className="text-[var(--d-muted)]">{(a.params || {}).milestone ? t(`milestone.${a.params.milestone}`) : a.template}</span>
                  <span className={`uppercase ${a.status === "sent" ? "text-[var(--d-accent)]" : "text-red-400"}`}>{a.status}</span>
                </div>
              ))}
            </Block>

            <Block title={t("dash.activity_block")} testid="dash-activity">
              {d.activity.length === 0 && <Empty testid="dash-activity-empty">{t("dash.activity_none")}</Empty>}
              {d.activity.map((a, i) => (
                <div key={i} className={`grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-[var(--d-line)] py-1.5 ${mono} text-[10px] last:border-b-0`} data-testid="dash-activity-row">
                  <span className="text-[var(--d-muted)]">{a.date}</span>
                  <span className="truncate text-[var(--d-text)]">{a.player_name || a.player_id}</span>
                  <span className="text-[var(--d-accent)]">{milestoneLabel(a, t)}</span>
                </div>
              ))}
            </Block>

            <Block title={t("dash.trends_block")} testid="dash-trends">
              {d.watchlist.length === 0 && <Empty testid="dash-trends-empty">{t("dash.trends_none")}</Empty>}
              {d.watchlist.map((w) => (
                <div key={`${w.provider}:${w.player_id}`} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-[var(--d-line)] py-2 last:border-b-0" data-testid="dash-trend-row">
                  <span className={`${mono} truncate text-xs text-[var(--d-text)]`}>{w.player?.name || w.name || t("dash.player_gone")}</span>
                  {w.signal_spark?.length >= 2 ? <Sparkline points={w.signal_spark} testid={`dash-spark-${w.player_id}`} /> : <span className={`${mono} text-[9px] text-[var(--d-muted)]`}>{t("dash.trend_no_history")}</span>}
                  <DeltaChip delta={w.signal_delta_7d} testid={`dash-delta-${w.player_id}`} />
                </div>
              ))}
            </Block>
          </>
        )}
      </div>
    </div>
  );
}
